// frontend/src/components/dashboard/pos/hooks/usePosCart.js
import { useState, useCallback } from 'react';

const usePosCart = () => {
  // ✅ FIXED: All state declarations MUST come first, in consistent order
  const [cartItems, setCartItems] = useState([]);

  // ✅ ENHANCED: Safe add to cart with proper price handling and unique ID generation
  const addToCart = useCallback((product) => {
    // SAFE: Ensure price is always a valid number
    const productPrice = parseFloat(product.finalPrice || product.price || 0);
    
    // SAFE: Create proper unique ID for items with/without variations
    const uniqueId = product.selectedVariation 
      ? `${product.id}-${product.selectedVariation.variation_id}` 
      : `${product.id}-base`;

    setCartItems(prevItems => {
      // SAFE: Find existing item by both product ID and variation ID
      const existingItemIndex = prevItems.findIndex(item => 
        item.id === product.id && 
        item.selectedVariation?.variation_id === product.selectedVariation?.variation_id
      );

      if (existingItemIndex > -1) {
        // Update existing item quantity
        const updatedItems = [...prevItems];
        updatedItems[existingItemIndex].quantity += 1;
        return updatedItems;
      } else {
        // Add new item with safe properties
        const newItem = {
          ...product,
          uniqueId,
          quantity: 1,
          price: productPrice, // Use the calculated final price as the main price
          finalPrice: productPrice, // Keep finalPrice for reference
          // SAFE: Ensure displayName exists
          displayName: product.displayName || 
                      (product.selectedVariation ? `${product.name} (${product.selectedVariation.name})` : product.name),
          // SAFE: Preserve variation info safely
          selectedVariation: product.selectedVariation ? {
            ...product.selectedVariation,
            price: parseFloat(product.selectedVariation.price || 0)
          } : null
        };
        
        return [...prevItems, newItem];
      }
    });
  }, []); // ✅ FIXED: Empty dependency array

  // ✅ ENHANCED: Safe increase quantity with proper uniqueId handling
  const increaseQuantity = useCallback((uniqueId) => {
    setCartItems(prevItems => 
      prevItems.map(item => 
        (item.uniqueId === uniqueId || item.id === uniqueId) // Support both uniqueId and legacy id
          ? { ...item, quantity: (item.quantity || 1) + 1 } 
          : item
      )
    );
  }, []); // ✅ FIXED: Empty dependency array

  // ✅ ENHANCED: Safe decrease quantity with proper uniqueId handling and removal
  const decreaseQuantity = useCallback((uniqueId) => {
    setCartItems(prevItems => 
      prevItems.map(item => 
        (item.uniqueId === uniqueId || item.id === uniqueId) && (item.quantity || 1) > 1
          ? { ...item, quantity: (item.quantity || 1) - 1 } 
          : item
      ).filter(item => (item.quantity || 1) > 0) // Remove items with 0 quantity
    );
  }, []); // ✅ FIXED: Empty dependency array

  // ✅ ENHANCED: Safe remove from cart with proper uniqueId handling
  const removeFromCart = useCallback((uniqueId) => {
    setCartItems(prevItems => 
      prevItems.filter(item => 
        item.uniqueId !== uniqueId && item.id !== uniqueId // Support both uniqueId and legacy id
      )
    );
  }, []); // ✅ FIXED: Empty dependency array

  // ✅ ENHANCED: Clear the cart (preserve existing functionality)
  const clearCart = useCallback(() => {
    setCartItems([]);
  }, []); // ✅ FIXED: Empty dependency array

  // ✅ ENHANCED: Safe calculate totals with proper number handling
  const calculateTotal = useCallback((includeServiceCharge = false, includeGST = false) => {
    // SAFE: Calculate subtotal with proper price and quantity validation
    const subtotal = cartItems.reduce((total, item) => {
      const itemPrice = parseFloat(item.finalPrice || item.price || 0);
      const itemQuantity = parseInt(item.quantity || 1);
      return total + (itemPrice * itemQuantity);
    }, 0);
    
    // SAFE: Calculate additional charges
    const serviceCharge = includeServiceCharge ? subtotal * 0.1 : 0;
    const gst = includeGST ? subtotal * 0.09 : 0;
    
    return {
      subtotal: parseFloat(subtotal.toFixed(2)),
      serviceCharge: parseFloat(serviceCharge.toFixed(2)),
      gst: parseFloat(gst.toFixed(2)),
      total: parseFloat((subtotal + serviceCharge + gst).toFixed(2))
    };
  }, [cartItems]); // ✅ FIXED: Proper dependency

  // ✅ NEW: Get cart item count (useful for badges)
  const getCartItemCount = useCallback(() => {
    return cartItems.reduce((count, item) => count + (item.quantity || 1), 0);
  }, [cartItems]); // ✅ FIXED: Proper dependency

  // ✅ NEW: Get cart total value (for quick display)
  const getCartTotalValue = useCallback((includeServiceCharge = false, includeGST = false) => {
    return calculateTotal(includeServiceCharge, includeGST).total;
  }, [calculateTotal]); // ✅ FIXED: Proper dependency

  // ✅ NEW: Check if product with variation exists in cart
  const isProductInCart = useCallback((productId, variationId = null) => {
    return cartItems.some(item => 
      item.id === productId && 
      item.selectedVariation?.variation_id === variationId
    );
  }, [cartItems]); // ✅ FIXED: Proper dependency

  // ✅ NEW: Get specific cart item
  const getCartItem = useCallback((uniqueId) => {
    return cartItems.find(item => 
      item.uniqueId === uniqueId || item.id === uniqueId
    );
  }, [cartItems]); // ✅ FIXED: Proper dependency

  // ✅ NEW: Update cart item (for future enhancements)
  const updateCartItem = useCallback((uniqueId, updates) => {
    setCartItems(prevItems =>
      prevItems.map(item =>
        (item.uniqueId === uniqueId || item.id === uniqueId)
          ? { ...item, ...updates }
          : item
      )
    );
  }, []); // ✅ FIXED: Empty dependency array

  // ✅ PRESERVE: Legacy function for backward compatibility
  const addToCartLegacy = useCallback((product) => {
    setCartItems(prevItems => {
      const existingItem = prevItems.find(item => item.id === product.id);
      
      if (existingItem) {
        return prevItems.map(item => 
          item.id === product.id 
            ? { ...item, quantity: (item.quantity || 1) + 1 } 
            : item
        );
      } else {
        return [...prevItems, { 
          ...product, 
          quantity: 1,
          uniqueId: `${product.id}-legacy-${Date.now()}`,
          price: parseFloat(product.price || 0),
          finalPrice: parseFloat(product.price || 0)
        }];
      }
    });
  }, []); // ✅ FIXED: Empty dependency array

  return {
    // Core cart state
    cartItems,
    
    // Main cart operations
    addToCart,
    increaseQuantity,
    decreaseQuantity,
    removeFromCart,
    clearCart,
    calculateTotal,
    
    // Additional utility functions
    getCartItemCount,
    getCartTotalValue,
    isProductInCart,
    getCartItem,
    updateCartItem,
    
    // Legacy support
    addToCartLegacy
  };
};

export default usePosCart;